import React, { useContext } from "react";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";
import { Tooltip } from "@mui/material";
import { AuthContext } from '../../../../../contexts/AuthContext';
import { PermissionModulesEnum, PermissionActionsEnum, Modules, AuthRoutes } from '../../../../../enums';
import { hasResourseAccess, formatNumber } from '../../../../../utils/methods';

interface StatsProps {
  data: any;
}

const Stats: React.FC<StatsProps> = ({ data }) => {
  const { t } = useTranslation();
  const { permissions, isSystemAdmin } = useContext(AuthContext);

  // Check permissions for each KPI type
  const canViewUsers = hasResourseAccess(isSystemAdmin, permissions, { accessibleTo: [{ [PermissionModulesEnum.Users]: [PermissionActionsEnum.View] }] });
  const canViewVendors = hasResourseAccess(isSystemAdmin, permissions, { accessibleTo: [{ [PermissionModulesEnum.Vendor]: [PermissionActionsEnum.View] }] });
  const canViewCustomers = hasResourseAccess(isSystemAdmin, permissions, { accessibleTo: [{ [PermissionModulesEnum.Customers]: [PermissionActionsEnum.View] }] });
  const canViewProperties = hasResourseAccess(isSystemAdmin, permissions, { accessibleTo: [{ [PermissionModulesEnum.Property]: [PermissionActionsEnum.View] }] });

  return (
    <>
      <div className="row mb-4">
        <div className="col-12">
          <h5 className="m-0 fw-bold">{t('layout.pageTexts.dashboard.pageTexts.resources.title')}</h5>
        </div>
      </div>
      <div className="row g-6 mb-6">
        {canViewUsers && (
          <div className="col-sm-6 col-xl-3">
            <Link to={Modules.resources + AuthRoutes.users} className="text-decoration-none">
              <div className="card cursor-pointer">
                <div className="card-body">
                  <div className="d-flex align-items-center flex-wrap">
                    <div className="avatar me-4">
                      <div className="avatar-initial bg-label-primary rounded-3">
                        <i className="ri ri-group-line icon-24px"></i>
                      </div>
                    </div>
                    <div className="card-info">
                      <div className="d-flex align-items-center">
                        <h5 className="mb-0 me-2">{formatNumber(data.totalUsers ?? 0)}</h5>
                      </div>
                      <p className="mb-0">{t('layout.pageTexts.dashboard.pageTexts.stats.titles.totalUsers')}</p>
                    </div>
                  </div>
                </div>
              </div>
            </Link>
          </div>
        )}
        {canViewVendors && (
          <div className="col-sm-6 col-xl-3">
            <Link to={Modules.resources + AuthRoutes.vendors} className="text-decoration-none">
              <div className="card cursor-pointer">
                <div className="card-body">
                  <div className="d-flex align-items-center flex-wrap">
                    <div className="avatar me-4">
                      <div className="avatar-initial bg-label-success rounded-3">
                        <i className="ri ri-store-line icon-24px"></i>
                      </div>
                    </div>
                    <div className="card-info">
                      <div className="d-flex align-items-center">
                        <h5 className="mb-0 me-2">{formatNumber(data.totalVendors ?? 0)}</h5>
                      </div>
                      <p className="mb-0">{t('layout.pageTexts.dashboard.pageTexts.stats.titles.totalVendors')}</p>
                    </div>
                  </div>
                </div>
              </div>
            </Link>
          </div>
        )}
        {canViewCustomers && (
          <div className="col-sm-6 col-xl-3">
            <Link to={Modules.resources + AuthRoutes.customers} className="text-decoration-none">
              <div className="card cursor-pointer">
                <div className="card-body">
                  <div className="d-flex align-items-center flex-wrap">
                    <div className="avatar me-4">
                      <div className="avatar-initial bg-label-warning rounded-3">
                        <i className="ri ri-user-line icon-24px"></i>
                      </div>
                    </div>
                    <div className="card-info">
                      <div className="d-flex align-items-center">
                        <h5 className="mb-0 me-2">{formatNumber(data.totalCustomers ?? 0)}</h5>
                      </div>
                      <p className="mb-0">{t('layout.pageTexts.dashboard.pageTexts.stats.titles.totalCustomers')}</p>
                    </div>
                  </div>
                </div>
              </div>
            </Link>
          </div>
        )}
        {canViewProperties && (
          <div className="col-sm-6 col-xl-3">
            <Link to={Modules.resources + AuthRoutes.properties} className="text-decoration-none">
              <div className="card cursor-pointer">
                <div className="card-body">
                  <div className="d-flex align-items-center flex-wrap">
                    <div className="avatar me-4">
                      <div className="avatar-initial bg-label-danger rounded-3">
                        <i className="ri ri-home-line icon-24px"></i>
                      </div>
                    </div>
                    <div className="card-info">
                      <div className="d-flex align-items-center">
                        <h5 className="mb-0 me-1">{formatNumber(data.myProperties ?? 0)}</h5>
                      </div>
                      <p className="mb-0">{t('layout.pageTexts.dashboard.pageTexts.stats.titles.myProperties')}</p>
                    </div>
                  </div>
                </div>
              </div>
            </Link>
          </div>
        )}
      </div>
    </>
  );
};

export default Stats;
